// Helpers for combining event date/time fields into real Date objects.
//
// These are shared by Create/Edit Event and the Staff Access Window presets.
// Centralized here so the overnight-roll logic (single-day event whose end
// time is earlier than its start time) lives in exactly one place.

/** Combine a "YYYY-MM-DD" date string + "HH:mm" time string into a local Date.
 *  Returns null if inputs are missing or malformed. */
export function combineDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  if (!y || !m || !d || hh == null || mm == null || Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Format a Date as "YYYY-MM-DDTHH:mm" suitable for <input type="datetime-local">. */
export function toLocalInput(dt) {
  if (!dt) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

/** Resolve the event's effective start datetime from form fields. */
export function resolveEventStartDateTime({ date, time }) {
  return combineDateTime(date, time);
}

/** Resolve the event's effective END datetime from form fields.
 *
 * Precedence:
 *   1. Multi-day event with explicit end_date → combine(end_date, end_time).
 *   2. Single-day event with end_time earlier than start_time (overnight) →
 *      combine(date, end_time) + 24h so the end lands on the next calendar day.
 *   3. Single-day event with well-formed end_time → combine(date, end_time).
 *
 * Returns null if we can't derive a meaningful end (e.g. missing date/time). */
export function resolveEventEndDateTime({ date, time, end_time, end_date, is_multi_day }) {
  if (is_multi_day && end_date) {
    // Multi-day: always honour the explicit end_date. Use end_time when
    // present, otherwise fall back to start time so the math still works.
    return combineDateTime(end_date, end_time || time);
  }
  if (!end_time) return null;

  const startDt = combineDateTime(date, time);
  const sameDayEnd = combineDateTime(date, end_time);
  if (!sameDayEnd) return null;

  // Overnight detection: single-day event whose end_time rolls past midnight.
  if (startDt && sameDayEnd.getTime() < startDt.getTime()) {
    return new Date(sameDayEnd.getTime() + 24 * 60 * 60 * 1000);
  }
  return sameDayEnd;
}
